import {cs} from "@common/react/chain-services";
import {consumeContext, provideContext} from "@common/react/context";
import {scope} from "@common/react/scope";
import {getPath} from "@common/utils/arr-path";
import {isEmpty} from "@common/utils/objects";
import * as React from "react";
import {Expandable} from "../../../../../../../common/expandable/expandable";
import {InfoDialog} from "../../../../../../../common/info-dialog/info-dialog";
import {getTileActionsUpdated} from "../actions/get-tile-actions-updated";
import {getTileFields} from "./common/get-tile-fields";
import {hasDateGroupsConfiguration} from "./common/has-date-groups-configuration";
import {getLimitUpdated} from "./common/limit-sorting/limit/get-limit-updated";
import {Limit} from "./common/limit-sorting/limit/limit";
import {AxisSorting} from "./common/limit-sorting/sorting/axis/axis-sorting";
import {getAxisSortingUpdated, getAxisSortingUpdatedByLimit} from "./common/limit-sorting/sorting/axis/get-axis-sorting-updated";
import {getGroupSortingUpdated} from "./common/limit-sorting/sorting/group/get-group-sorting-updated";
import {GroupSorting} from "./common/limit-sorting/sorting/group/group-sorting";
import {MeasureFieldGroups} from "./common/measure-field-groups/measure-field-groups";
import {getColumnType} from "./common/select-field/select-field";
import {SingleFieldWell} from "./common/single-field-well/single-field-well";
import {updateLimitSortingInTile, updateMeasureFieldsGroupsInTile, updateSingleFieldInTile} from "./common/update-tile";
import "./tile-fields.scss";

export const ComboChartFields = ({tile, isSubTile, form, next, tileActions, debugFunction}) =>
    cs(
        ["infoDialog", (_, next) => InfoDialog({next})],
        consumeContext("getFieldInfo"),
        [
            "tileFields",
            ({getFieldInfo}, next) =>
                next(
                    getTileFields({
                        tile: tile.value,
                        dimensionAttr: "xAxisField",
                        measureGroupAttrs: ["yAxisBarFields", "yAxisLineFields"],
                        groupFieldAttr: "groupField",
                        getFieldInfo,
                    })
                ),
        ],
        ({tileFields}, next) => provideContext("tileFields", tileFields, next),

        [
            "xAxisField",
            ({tileFields}, next) =>
                SingleFieldWell({
                    field: scope(tile, ["xAxisField"]),
                    $type: "DimensionTileField",
                    label: "X-Axis",
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "xAxisField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    next,
                }),
        ],
        [
            "yAxisBarFields",
            ({infoDialog, tileFields}, next) =>
                MeasureFieldGroups({
                    fieldGroups: scope(tile, ["yAxisBarFields"]),
                    label: "Y-Axis Bar",
                    onUpdateTile: (newVal, indexes) =>
                        updateMeasureFieldsGroupsInTile({
                            fieldAttr: "yAxisBarFields",
                            fieldVal: {newVal, oriVal: null},
                            tileFields,
                            indexes,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                        }),
                    unavailable: () => !isEmpty(tile.value?.groupField),
                    showUnavailableMessage: () =>
                        infoDialog.show({
                            title: "Grouped Bar Unavailable",
                            content: `The grouped bar option is not available because there is a group field selected. To create a grouped bar joined with an "AND" first remove the group field.`,
                        }),
                    info: (
                        <span>
                            The <b>OR</b> option will create a separate axis on the chart. You may control how multiple axes display in the Display tab, Multiple Axis Display
                            option.
                        </span>
                    ),
                    direction: "top",
                    next,
                }),
        ],
        [
            "yAxisLineFields",
            ({tileFields}, next) =>
                MeasureFieldGroups({
                    fieldGroups: scope(tile, ["yAxisLineFields"]),
                    label: "Y-Axis Line",
                    onUpdateTile: (newVal, indexes) =>
                        updateMeasureFieldsGroupsInTile({
                            fieldAttr: "yAxisLineFields",
                            fieldVal: {newVal, oriVal: null},
                            tileFields,
                            indexes,
                            tile,
                            updateSorting: getAxisSortingUpdated,
                            updateLimit: getLimitUpdated,
                        }),
                    info: (
                        <span>
                            The <b>OR</b> option will create a separate axis on the chart. You may control how multiple axes display in the Display tab, Multiple Axis Display
                            option.
                        </span>
                    ),
                    direction: "top",
                    next,
                }),
        ],
        [
            "groupField",
            ({infoDialog, tileFields}, next) =>
                SingleFieldWell({
                    field: scope(tile, ["groupField"]),
                    hasDateGroupsConfiguration: hasDateGroupsConfiguration(tile.value.$type),
                    $type: "CategoryTileField",
                    label: "Group",
                    filterColumn: (column) => column.$type !== "AggregatedMeasureModelColumn",
                    onUpdateTile: (newVal, oriVal) =>
                        updateSingleFieldInTile({
                            fieldAttr: "groupField",
                            fieldVal: {newVal, oriVal},
                            tileFields,
                            tile,
                            updateSorting: getGroupSortingUpdated,
                            updateTileActions: getTileActionsUpdated,
                        }),
                    unavailable: () => tile.value?.yAxisBarFields?.filter((f) => f.measureFields?.length > 1).length > 0,
                    showUnavailableMessage: () =>
                        infoDialog.show({
                            title: "Grouped Field Unavailable",
                            content: `The group field is not available because there is a grouped bar that is joined with an "AND". To add a group field first remove any grouped bars.`,
                        }),
                    next,
                }),
        ],
        [
            "fieldsSection",
            ({xAxisField, yAxisBarFields, yAxisLineFields, groupField}, next) =>
                next({
                    render: () =>
                        Expandable({
                            label: "Fields",
                            render: () => (
                                <>
                                    {xAxisField.render()}
                                    {yAxisBarFields.render()}
                                    {yAxisLineFields.render()}
                                    {groupField.render()}
                                </>
                            ),
                        }),
                    override: xAxisField.override || yAxisBarFields.override || yAxisLineFields.override || groupField.override,
                }),
        ],

        [
            "limitSorting",
            ({tileFields}, next) =>
                cs(
                    [
                        "limit",
                        (_, next) => {
                            const formField = form.field(["limit"]);
                            return next(
                                Limit({
                                    allFields: tileFields.allFieldsWithoutUniq,
                                    field: formField.state,
                                    onChange: (newLimit, field) => {
                                        const limit = {
                                            ...newLimit,
                                            direction: field.$type === "MeasureTileField" ? "Asc" : newLimit.direction,
                                        };

                                        updateLimitSortingInTile({
                                            tile,
                                            limit,
                                            updateSorting: () =>
                                                getAxisSortingUpdatedByLimit({
                                                    tile,
                                                    field: {
                                                        ...limit,
                                                        $type: field.$type,
                                                    },
                                                    dimensionFieldAttr: "xAxisField",
                                                }),
                                        });
                                    },
                                    isHiddenRankOption: () => getPath(formField.state.value, ["modelColumnID"]) == getPath(tile.value, ["xAxisField", "modelColumnID"]),
                                })
                            );
                        },
                    ],
                    [
                        "sorting",
                        (_, next) =>
                            next(
                                <>
                                    {AxisSorting({
                                        fieldGroups: form.field(["sort"]).state,
                                        title: "X-Axis",
                                    })}
                                    {tile.value.groupField && GroupSorting({form, tile})}
                                </>
                            ),
                    ],
                    ({limit, sorting}) =>
                        next({
                            render: () =>
                                Expandable({
                                    initExpand: false,
                                    label: "Limit & Sorting",
                                    render: () => (
                                        <>
                                            {limit}
                                            {sorting}
                                        </>
                                    ),
                                }),
                        })
                ),
        ],

        ({limitSorting, fieldsSection}) =>
            next({
                render: () => (
                    <div className="tile-fields-3vr">
                        {fieldsSection.render()}
                        {limitSorting.render()}
                        {tileActions?.render?.()}
                        {isSubTile && debugFunction?.render?.({})}
                    </div>
                ),
                override: fieldsSection.override || debugFunction?.override() || tileActions?.override({fieldType: "MeasureTileField"}),
                overrideAll: debugFunction?.overrideAll || tileActions?.overrideAll,
            })
    );
